import React, { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./leafletMinimap.css";

import { createEsriImageryTileLayer } from "components/maps/esriTileLayer";

import gpsIcon from "assets/images/gps.png";
import gpsActiveIcon from "assets/images/gps_active.png";

function LeafletMinimap({ images, activeMarker, onMarkerClick }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  // Guarda o conjunto de coordenadas já enquadrado, para não repetir fitBounds.
  const fittedBoundsKeyRef = useRef(null);
  // O tour recria onMarkerClick em todo render; lido via ref, os marcadores não
  // precisam ser recriados junto.
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;

  // Os ícones precisam de identidade estável: criados no corpo do componente,
  // mudavam a cada render e faziam o efeito abaixo recriar todos os marcadores e
  // chamar fitBounds em todo render — inclusive a cada mousemove do arraste,
  // disparando centenas de requisições de tiles por segundo.
  const defaultIcon = useMemo(
    () => L.icon({ iconUrl: gpsIcon, iconSize: [24, 24] }),
    []
  );

  const activeIcon = useMemo(
    () => L.icon({ iconUrl: gpsActiveIcon, iconSize: [28, 28] }),
    []
  );

  // Cria o mapa uma única vez.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    mapRef.current = L.map(containerRef.current, {
      center: [0, 0],
      zoom: 5,
      zoomControl: true,
      dragging: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      touchZoom: true
    });

    createEsriImageryTileLayer({ maxZoom: 19 }).addTo(mapRef.current);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = [];
      fittedBoundsKeyRef.current = null;
    };
  }, []);

  const points = useMemo(
    () =>
      (images || [])
        .map((img, index) => ({
          index,
          lat: Number(img.latitude),
          lng: Number(img.longitude)
        }))
        .filter(p => !isNaN(p.lat) && !isNaN(p.lng)),
    [images]
  );

  // Recria os marcadores só quando as coordenadas mudam.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach(({ marker }) => map.removeLayer(marker));
    markersRef.current = points.map(p => {
      const marker = L.marker([p.lat, p.lng], { icon: defaultIcon }).addTo(map);
      marker.on("click", () => onMarkerClickRef.current(p.index));
      return { index: p.index, marker };
    });

    if (points.length > 0) {
      // Só enquadra quando o conjunto de pontos é novo. Antes o fitBounds rodava
      // em todo render, remexendo o zoom e recarregando tiles sem necessidade.
      const boundsKey = points.map(p => `${p.lat},${p.lng}`).join("|");
      if (fittedBoundsKeyRef.current !== boundsKey) {
        map.fitBounds(points.map(p => [p.lat, p.lng]), { padding: [20, 20] });
        fittedBoundsKeyRef.current = boundsKey;
      }
    }
  }, [points, defaultIcon]);

  // Troca apenas o ícone do marcador ativo, sem tocar no mapa nem no zoom.
  useEffect(() => {
    markersRef.current.forEach(({ index, marker }) => {
      marker.setIcon(index === activeMarker ? activeIcon : defaultIcon);
    });
  }, [activeMarker, activeIcon, defaultIcon]);

  return <div ref={containerRef} className="leaflet-minimap" />;
}

// O wrapper arrastável atualiza sua posição a cada mousemove; sem memo, cada um
// desses renders se propagava até aqui.
export default React.memo(LeafletMinimap);
